import { useState, useMemo } from 'react'
import {
  useShortLinks, useShortLinkStats, useCreateShortLink,
  useUpdateShortLink, useDeleteShortLink,
  useAddShortLinkVideo, useDeleteShortLinkVideo,
  type ShortLink, type ShortLinkVideo, type UtmParams,
} from '@/api/hooks/useShortLinks'
import { useMediaFolders } from '@/api/hooks/useMediaLibrary'
import { useLanguage } from '@/i18n/LanguageContext'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { Textarea } from '@/components/ui/textarea'
import { Switch } from '@/components/ui/switch'
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select'
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog'
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table'
import {
  Collapsible, CollapsibleContent, CollapsibleTrigger,
} from '@/components/ui/collapsible'
import {
  Link2, MousePointerClick, Plus, Copy, Trash2, ExternalLink,
  Pencil, ToggleLeft, ToggleRight, Check, ChevronDown, Image as ImageIcon,
  FolderOpen, X, Eye,
} from 'lucide-react'
import { toast } from 'sonner'

const maskLabels: Record<string, string> = {
  none: 'Nenhuma',
  twitter: 'Twitter / X',
  telegram: 'Telegram',
  instagram: 'Instagram',
  tiktok: 'TikTok',
}

const maskColors: Record<string, string> = {
  none: 'bg-muted text-muted-foreground',
  twitter: 'bg-blue-500/10 text-blue-500 border-blue-500/20',
  telegram: 'bg-sky-500/10 text-sky-500 border-sky-500/20',
  instagram: 'bg-pink-500/10 text-pink-500 border-pink-500/20',
  tiktok: 'bg-foreground/10 text-foreground border-foreground/20',
}

const fmtDate = new Intl.DateTimeFormat('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' })

/* ─── Media Picker (supports image and video) ─── */
function MediaPicker({
  open, onClose, onSelect, mediaType = 'image',
}: {
  open: boolean
  onClose: () => void
  onSelect: (url: string) => void
  mediaType?: 'image' | 'video'
}) {
  const { t } = useLanguage()
  const { data: folders } = useMediaFolders()
  const [selectedFolderId, setSelectedFolderId] = useState('')

  const filteredFolders = useMemo(() =>
    (folders ?? []).filter(f => f.images.some(img => img.mediaType === mediaType)),
  [folders, mediaType])

  const selectedFolder = filteredFolders.find(f => f.id === selectedFolderId)
  const items = selectedFolder?.images.filter(img => img.mediaType === mediaType) ?? []

  return (
    <Dialog open={open} onOpenChange={v => !v && onClose()}>
      <DialogContent className="sm:max-w-lg max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="text-sm">
            {mediaType === 'video' ? t('Escolher video da biblioteca') : t('Escolher imagem da biblioteca')}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3 flex-1 overflow-hidden flex flex-col">
          <Select value={selectedFolderId} onValueChange={setSelectedFolderId}>
            <SelectTrigger className="text-sm">
              <SelectValue placeholder={t('Selecione uma pasta')} />
            </SelectTrigger>
            <SelectContent>
              {filteredFolders.map(f => (
                <SelectItem key={f.id} value={f.id}>
                  <span className="flex items-center gap-2">
                    <FolderOpen className="h-3 w-3" />
                    {f.name}
                    <span className="text-muted-foreground text-[10px]">
                      ({f.images.filter(i => i.mediaType === mediaType).length})
                    </span>
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {selectedFolder ? (
            items.length > 0 ? (
              <div className="grid grid-cols-4 gap-2 overflow-y-auto flex-1 pr-1">
                {items.map(img => (
                  <button
                    key={img.id}
                    type="button"
                    onClick={() => { onSelect(img.url); onClose() }}
                    className="aspect-square rounded-md overflow-hidden border border-border hover:border-primary hover:ring-2 hover:ring-primary/30 transition-all"
                  >
                    {mediaType === 'video' ? (
                      <div className="w-full h-full bg-muted/50 flex flex-col items-center justify-center gap-1 p-1">
                        <span className="text-xl">🎬</span>
                        <span className="text-[8px] text-muted-foreground truncate w-full text-center">{img.filename}</span>
                      </div>
                    ) : (
                      <img
                        src={img.url}
                        alt={img.filename}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    )}
                  </button>
                ))}
              </div>
            ) : (
              <p className="text-xs text-muted-foreground text-center py-8">
                {mediaType === 'video' ? t('Nenhum video nesta pasta') : t('Nenhuma imagem nesta pasta')}
              </p>
            )
          ) : (
            <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
              <ImageIcon className="h-8 w-8 mb-2 opacity-20" />
              <p className="text-xs">{t('Selecione uma pasta para ver os arquivos')}</p>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" size="sm" onClick={onClose}>{t('Cancelar')}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/* ─── Link Create/Edit Dialog ─── */
function LinkDialog({
  open, onOpenChange, editLink,
}: {
  open: boolean
  onOpenChange: (v: boolean) => void
  editLink: ShortLink | null
}) {
  const { t } = useLanguage()
  const createMut = useCreateShortLink()
  const updateMut = useUpdateShortLink()
  const addVideoMut = useAddShortLinkVideo()
  const deleteVideoMut = useDeleteShortLinkVideo()

  const [targetUrl, setTargetUrl] = useState(editLink?.targetUrl ?? '')
  const [mask, setMask] = useState(editLink?.mask ?? 'none')
  const [ogTitle, setOgTitle] = useState(editLink?.ogTitle ?? '')
  const [ogDescription, setOgDescription] = useState(editLink?.ogDescription ?? '')
  const [ogImage, setOgImage] = useState(editLink?.ogImage ?? '')

  const [utmSource, setUtmSource] = useState(editLink?.utmParams?.utm_source ?? '')
  const [utmMedium, setUtmMedium] = useState(editLink?.utmParams?.utm_medium ?? '')
  const [utmCampaign, setUtmCampaign] = useState(editLink?.utmParams?.utm_campaign ?? '')
  const [utmTerm, setUtmTerm] = useState(editLink?.utmParams?.utm_term ?? '')
  const [utmContent, setUtmContent] = useState(editLink?.utmParams?.utm_content ?? '')

  const [utmOpen, setUtmOpen] = useState(!!editLink?.utmParams)
  const [imagePickerOpen, setImagePickerOpen] = useState(false)

  // LP state
  const [lpEnabled, setLpEnabled] = useState(editLink?.lpEnabled ?? false)
  const [lpDomain, setLpDomain] = useState(editLink?.domain ?? 'default')
  const [lpRedirectMode, setLpRedirectMode] = useState(editLink?.lpRedirectMode ?? 'auto')
  const [lpRedirectDelay, setLpRedirectDelay] = useState(editLink?.lpRedirectDelay ?? 10)
  const [lpSiteName, setLpSiteName] = useState(editLink?.lpSiteName ?? '')
  const [lpSiteColor, setLpSiteColor] = useState(editLink?.lpSiteColor ?? '#cc0000')
  const [lpOpen, setLpOpen] = useState(editLink?.lpEnabled ?? false)

  // Video management
  const [videos, setVideos] = useState<ShortLinkVideo[]>(editLink?.videos ?? [])
  const [addVideoOpen, setAddVideoOpen] = useState(false)
  const [videoPickerOpen, setVideoPickerOpen] = useState(false)
  const [thumbPickerOpen, setThumbPickerOpen] = useState(false)
  const [newVideoTitle, setNewVideoTitle] = useState('')
  const [newVideoUrl, setNewVideoUrl] = useState('')
  const [newThumbUrl, setNewThumbUrl] = useState('')
  const [newDuration, setNewDuration] = useState('10:00')
  const [newViews, setNewViews] = useState('1.2M views')
  const [newRating, setNewRating] = useState('94%')

  const saving = createMut.isPending || updateMut.isPending

  const hasUtm = utmSource || utmMedium || utmCampaign || utmTerm || utmContent

  const handleOpenChange = (v: boolean) => {
    if (v) {
      setTargetUrl(editLink?.targetUrl ?? '')
      setMask(editLink?.mask ?? 'none')
      setOgTitle(editLink?.ogTitle ?? '')
      setOgDescription(editLink?.ogDescription ?? '')
      setOgImage(editLink?.ogImage ?? '')
      setUtmSource(editLink?.utmParams?.utm_source ?? '')
      setUtmMedium(editLink?.utmParams?.utm_medium ?? '')
      setUtmCampaign(editLink?.utmParams?.utm_campaign ?? '')
      setUtmTerm(editLink?.utmParams?.utm_term ?? '')
      setUtmContent(editLink?.utmParams?.utm_content ?? '')
      setUtmOpen(!!editLink?.utmParams)
      setLpEnabled(editLink?.lpEnabled ?? false)
      setLpDomain(editLink?.domain ?? 'default')
      setLpRedirectMode(editLink?.lpRedirectMode ?? 'auto')
      setLpRedirectDelay(editLink?.lpRedirectDelay ?? 10)
      setLpSiteName(editLink?.lpSiteName ?? '')
      setLpSiteColor(editLink?.lpSiteColor ?? '#cc0000')
      setLpOpen(editLink?.lpEnabled ?? false)
      setVideos(editLink?.videos ?? [])
    }
    onOpenChange(v)
  }

  const handleSave = () => {
    if (!targetUrl.trim()) {
      toast.error(t('URL de destino e obrigatoria'))
      return
    }

    const utmParams: UtmParams | undefined = hasUtm ? {
      ...(utmSource && { utm_source: utmSource.trim() }),
      ...(utmMedium && { utm_medium: utmMedium.trim() }),
      ...(utmCampaign && { utm_campaign: utmCampaign.trim() }),
      ...(utmTerm && { utm_term: utmTerm.trim() }),
      ...(utmContent && { utm_content: utmContent.trim() }),
    } : undefined

    const data = {
      targetUrl: targetUrl.trim(),
      mask,
      ogTitle: ogTitle.trim() || undefined,
      ogDescription: ogDescription.trim() || undefined,
      ogImage: ogImage.trim() || undefined,
      utmParams,
      domain: lpDomain === 'default' ? undefined : lpDomain,
      lpEnabled,
      lpRedirectMode,
      lpRedirectDelay,
      lpSiteName: lpSiteName.trim() || undefined,
      lpSiteColor: lpSiteColor || undefined,
    }

    if (editLink) {
      updateMut.mutate({ id: editLink.id, ...data }, {
        onSuccess: () => { toast.success(t('Link atualizado')); handleOpenChange(false) },
        onError: (e) => toast.error(e.message),
      })
    } else {
      createMut.mutate(data, {
        onSuccess: () => { toast.success(t('Link criado')); handleOpenChange(false) },
        onError: (e) => toast.error(e.message),
      })
    }
  }

  const handleAddVideo = () => {
    if (!editLink) return
    addVideoMut.mutate({
      linkId: editLink.id,
      title: newVideoTitle,
      thumbnailUrl: newThumbUrl,
      videoUrl: newVideoUrl,
      durationLabel: newDuration,
      viewsLabel: newViews,
      ratingLabel: newRating,
    }, {
      onSuccess: (v) => {
        setVideos(prev => [...prev, v])
        setAddVideoOpen(false)
        setNewVideoTitle(''); setNewVideoUrl(''); setNewThumbUrl('')
        setNewDuration('10:00'); setNewViews('1.2M views'); setNewRating('94%')
        toast.success(t('Video adicionado'))
      },
      onError: (e) => toast.error(e.message),
    })
  }

  const handleDeleteVideo = (videoId: string) => {
    if (!editLink) return
    deleteVideoMut.mutate({ linkId: editLink.id, videoId }, {
      onSuccess: () => setVideos(prev => prev.filter(x => x.id !== videoId)),
    })
  }

  return (
    <>
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent className="sm:max-w-md max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editLink ? t('Editar Link') : t('Novo Link Curto')}</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-2">
            {/* URL de destino */}
            <div className="space-y-1.5">
              <Label className="text-xs">{t('URL de destino')}</Label>
              <Input
                value={targetUrl}
                onChange={e => setTargetUrl(e.target.value)}
                placeholder="https://exemplo.com/pagina"
                className="text-sm"
              />
            </div>

            {/* Mascara */}
            <div className="space-y-1.5">
              <Label className="text-xs">{t('Mascara de rede social')}</Label>
              <Select value={mask} onValueChange={v => setMask(v)}>
                <SelectTrigger className="text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(maskLabels).map(([key, label]) => (
                    <SelectItem key={key} value={key}>{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[10px] text-muted-foreground">
                {t('Quando compartilhado, o preview vai parecer ser dessa rede')}
              </p>
            </div>

            {/* OG fields - visible when mask != none */}
            {mask !== 'none' && (
              <>
                <div className="space-y-1.5">
                  <Label className="text-xs">{t('Titulo do preview')} ({t('opcional')})</Label>
                  <Input
                    value={ogTitle}
                    onChange={e => setOgTitle(e.target.value)}
                    placeholder={maskLabels[mask]}
                    className="text-sm"
                  />
                </div>

                <div className="space-y-1.5">
                  <Label className="text-xs">{t('Descricao do preview')} ({t('opcional')})</Label>
                  <Textarea
                    value={ogDescription}
                    onChange={e => setOgDescription(e.target.value)}
                    placeholder={t('Texto que aparece no card do preview')}
                    className="text-sm resize-none"
                    rows={2}
                  />
                </div>

                <div className="space-y-1.5">
                  <Label className="text-xs">{t('Imagem do preview')} ({t('opcional')})</Label>
                  <div className="flex gap-2">
                    <Input
                      value={ogImage}
                      onChange={e => setOgImage(e.target.value)}
                      placeholder="https://..."
                      className="text-sm flex-1"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setImagePickerOpen(true)}
                      title={t('Escolher da biblioteca')}
                    >
                      <ImageIcon className="h-4 w-4" />
                    </Button>
                  </div>
                  {ogImage && (
                    <div className="relative mt-1.5 w-20 h-20 rounded-md overflow-hidden border border-border">
                      <img src={ogImage} alt="Preview" className="w-full h-full object-cover" />
                      <button
                        type="button"
                        onClick={() => setOgImage('')}
                        className="absolute top-0.5 right-0.5 bg-black/60 rounded-full p-0.5 text-white hover:bg-black/80"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  )}
                  <p className="text-[10px] text-muted-foreground">
                    {t('Cole uma URL ou escolha da biblioteca de midias')}
                  </p>
                </div>
              </>
            )}

            {/* UTM Parameters - collapsible */}
            <Collapsible open={utmOpen} onOpenChange={setUtmOpen}>
              <CollapsibleTrigger asChild>
                <button
                  type="button"
                  className="flex items-center gap-2 w-full text-xs font-medium text-muted-foreground hover:text-foreground transition-colors py-1"
                >
                  <ChevronDown className={cn('h-3.5 w-3.5 transition-transform', utmOpen && 'rotate-180')} />
                  {t('Parametros UTM')}
                  {hasUtm && !utmOpen && (
                    <Badge variant="secondary" className="text-[9px] ml-1">{t('configurado')}</Badge>
                  )}
                </button>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <div className="space-y-3 pt-2 pl-1 border-l-2 border-border ml-1.5">
                  <div className="space-y-1.5 pl-3">
                    <Label className="text-[10px] text-muted-foreground">utm_source</Label>
                    <Input value={utmSource} onChange={e => setUtmSource(e.target.value)} placeholder="facebook, google, newsletter..." className="text-sm h-8" />
                  </div>
                  <div className="space-y-1.5 pl-3">
                    <Label className="text-[10px] text-muted-foreground">utm_medium</Label>
                    <Input value={utmMedium} onChange={e => setUtmMedium(e.target.value)} placeholder="social, cpc, email..." className="text-sm h-8" />
                  </div>
                  <div className="space-y-1.5 pl-3">
                    <Label className="text-[10px] text-muted-foreground">utm_campaign</Label>
                    <Input value={utmCampaign} onChange={e => setUtmCampaign(e.target.value)} placeholder="lancamento-produto, black-friday..." className="text-sm h-8" />
                  </div>
                  <div className="space-y-1.5 pl-3">
                    <Label className="text-[10px] text-muted-foreground">utm_term</Label>
                    <Input value={utmTerm} onChange={e => setUtmTerm(e.target.value)} placeholder="palavra-chave..." className="text-sm h-8" />
                  </div>
                  <div className="space-y-1.5 pl-3">
                    <Label className="text-[10px] text-muted-foreground">utm_content</Label>
                    <Input value={utmContent} onChange={e => setUtmContent(e.target.value)} placeholder="banner-topo, link-bio..." className="text-sm h-8" />
                  </div>
                </div>
              </CollapsibleContent>
            </Collapsible>

            {/* Landing Page - collapsible */}
            <Collapsible open={lpOpen} onOpenChange={setLpOpen}>
              <CollapsibleTrigger asChild>
                <button
                  type="button"
                  className="flex items-center gap-2 w-full text-xs font-medium text-muted-foreground hover:text-foreground transition-colors py-1"
                >
                  <ChevronDown className={cn('h-3.5 w-3.5 transition-transform', lpOpen && 'rotate-180')} />
                  {t('Landing Page')}
                  {lpEnabled && !lpOpen && (
                    <Badge variant="secondary" className="text-[9px] ml-1 bg-red-500/10 text-red-500 border-red-500/20">{t('ativo')}</Badge>
                  )}
                </button>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <div className="space-y-3 pt-2 pl-1 border-l-2 border-red-500/30 ml-1.5">
                  {/* Toggle */}
                  <div className="flex items-center gap-2 pl-3">
                    <Switch checked={lpEnabled} onCheckedChange={setLpEnabled} />
                    <Label className="text-xs">{t('Habilitar Landing Page')}</Label>
                  </div>

                  {lpEnabled && (
                    <>
                      {/* Domain */}
                      <div className="space-y-1.5 pl-3">
                        <Label className="text-[10px] text-muted-foreground">{t('Dominio')}</Label>
                        <Select value={lpDomain} onValueChange={setLpDomain}>
                          <SelectTrigger className="text-sm h-8">
                            <SelectValue placeholder="blissf.com" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="default">blissf.com</SelectItem>
                            <SelectItem value="r.forexbrokereview.com">r.forexbrokereview.com</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      {/* Redirect mode */}
                      <div className="space-y-1.5 pl-3">
                        <Label className="text-[10px] text-muted-foreground">{t('Modo de redirect')}</Label>
                        <Select value={lpRedirectMode} onValueChange={v => setLpRedirectMode(v as 'auto' | 'button')}>
                          <SelectTrigger className="text-sm h-8"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="auto">{t('Auto (redireciona automaticamente)')}</SelectItem>
                            <SelectItem value="button">{t('Botao (usuario clica para continuar)')}</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      {/* Delay */}
                      <div className="space-y-1.5 pl-3">
                        <Label className="text-[10px] text-muted-foreground">{t('Tempo antes do redirect')} (s)</Label>
                        <Input
                          type="number" min={3} max={120}
                          value={lpRedirectDelay}
                          onChange={e => setLpRedirectDelay(Number(e.target.value))}
                          className="text-sm h-8 w-24"
                        />
                      </div>

                      {/* Site name */}
                      <div className="space-y-1.5 pl-3">
                        <Label className="text-[10px] text-muted-foreground">{t('Nome do site')}</Label>
                        <Input value={lpSiteName} onChange={e => setLpSiteName(e.target.value)} placeholder="VideoHub" className="text-sm h-8" />
                      </div>

                      {/* Accent color */}
                      <div className="space-y-1.5 pl-3">
                        <Label className="text-[10px] text-muted-foreground">{t('Cor accent')}</Label>
                        <div className="flex items-center gap-2">
                          <input
                            type="color" value={lpSiteColor}
                            onChange={e => setLpSiteColor(e.target.value)}
                            className="w-8 h-8 rounded cursor-pointer border border-border"
                          />
                          <Input value={lpSiteColor} onChange={e => setLpSiteColor(e.target.value)} className="text-sm h-8 w-28 font-mono" maxLength={7} />
                        </div>
                      </div>

                      {/* Videos */}
                      <div className="pl-3 space-y-2">
                        <div className="flex items-center justify-between">
                          <Label className="text-[10px] text-muted-foreground">{t('Videos')} ({videos.length})</Label>
                          {editLink && (
                            <Button type="button" variant="outline" size="sm" className="h-6 text-[10px]" onClick={() => setAddVideoOpen(true)}>
                              <Plus className="h-3 w-3 mr-1" />{t('Adicionar')}
                            </Button>
                          )}
                        </div>

                        {!editLink && (
                          <p className="text-[10px] text-muted-foreground">{t('Salve o link primeiro, depois adicione videos')}</p>
                        )}

                        {videos.length > 0 && (
                          <div className="space-y-1">
                            {videos.map(v => (
                              <div key={v.id} className="flex items-center gap-2 bg-muted/30 rounded p-1.5">
                                <img src={v.thumbnailUrl} alt="" className="w-12 h-8 rounded object-cover flex-shrink-0" />
                                <div className="flex-1 min-w-0">
                                  <p className="text-[11px] truncate">{v.title}</p>
                                  <p className="text-[9px] text-muted-foreground">{v.durationLabel} - {v.viewsLabel}</p>
                                </div>
                                <button
                                  type="button"
                                  onClick={() => handleDeleteVideo(v.id)}
                                  className="text-muted-foreground hover:text-destructive p-0.5 flex-shrink-0"
                                >
                                  <Trash2 className="h-3 w-3" />
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </div>
              </CollapsibleContent>
            </Collapsible>
          </div>

          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => handleOpenChange(false)}>{t('Cancelar')}</Button>
            <Button size="sm" onClick={handleSave} disabled={saving}>
              {saving ? t('Salvando...') : editLink ? t('Salvar') : t('Criar')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {imagePickerOpen && (
        <MediaPicker
          open={imagePickerOpen}
          onClose={() => setImagePickerOpen(false)}
          onSelect={setOgImage}
          mediaType="image"
        />
      )}

      {/* Add Video Dialog */}
      {addVideoOpen && editLink && (
        <Dialog open={addVideoOpen} onOpenChange={setAddVideoOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader><DialogTitle className="text-sm">{t('Adicionar Video')}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div className="space-y-1.5">
                <Label className="text-xs">{t('Video (da biblioteca)')}</Label>
                <div className="flex gap-2">
                  <Input value={newVideoUrl} readOnly placeholder="URL do video..." className="text-sm flex-1" />
                  <Button type="button" variant="outline" size="sm" onClick={() => setVideoPickerOpen(true)}>
                    {t('Escolher')}
                  </Button>
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">{t('Thumbnail (imagem)')}</Label>
                <div className="flex gap-2">
                  <Input value={newThumbUrl} readOnly placeholder="URL do thumb..." className="text-sm flex-1" />
                  <Button type="button" variant="outline" size="sm" onClick={() => setThumbPickerOpen(true)}>
                    {t('Escolher')}
                  </Button>
                </div>
                {newThumbUrl && <img src={newThumbUrl} alt="" className="w-16 h-10 rounded object-cover mt-1" />}
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">{t('Titulo')}</Label>
                <Input value={newVideoTitle} onChange={e => setNewVideoTitle(e.target.value)} placeholder="Titulo do video" className="text-sm" />
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <Label className="text-[10px] text-muted-foreground">{t('Duracao')}</Label>
                  <Input value={newDuration} onChange={e => setNewDuration(e.target.value)} className="text-sm h-8" />
                </div>
                <div>
                  <Label className="text-[10px] text-muted-foreground">Views</Label>
                  <Input value={newViews} onChange={e => setNewViews(e.target.value)} className="text-sm h-8" />
                </div>
                <div>
                  <Label className="text-[10px] text-muted-foreground">Rating</Label>
                  <Input value={newRating} onChange={e => setNewRating(e.target.value)} className="text-sm h-8" />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" size="sm" onClick={() => setAddVideoOpen(false)}>{t('Cancelar')}</Button>
              <Button
                size="sm"
                disabled={!newVideoUrl || !newThumbUrl || !newVideoTitle || addVideoMut.isPending}
                onClick={handleAddVideo}
              >
                {addVideoMut.isPending ? t('Adicionando...') : t('Adicionar')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {videoPickerOpen && (
        <MediaPicker open={videoPickerOpen} onClose={() => setVideoPickerOpen(false)} onSelect={(url) => { setNewVideoUrl(url); setVideoPickerOpen(false) }} mediaType="video" />
      )}
      {thumbPickerOpen && (
        <MediaPicker open={thumbPickerOpen} onClose={() => setThumbPickerOpen(false)} onSelect={(url) => { setNewThumbUrl(url); setThumbPickerOpen(false) }} mediaType="image" />
      )}
    </>
  )
}

/* ─── Main Page ─── */
export function ShortLinks() {
  const { t } = useLanguage()
  const { data: links, isLoading } = useShortLinks()
  const { data: stats } = useShortLinkStats()
  const updateMut = useUpdateShortLink()
  const deleteMut = useDeleteShortLink()

  const [dialogOpen, setDialogOpen] = useState(false)
  const [editLink, setEditLink] = useState<ShortLink | null>(null)
  const [copiedId, setCopiedId] = useState<string | null>(null)

  const copyToClipboard = (link: ShortLink) => {
    navigator.clipboard.writeText(link.shortUrl)
    setCopiedId(link.id)
    toast.success(t('Link copiado'))
    setTimeout(() => setCopiedId(null), 2000)
  }

  const toggleActive = (link: ShortLink) => {
    updateMut.mutate({ id: link.id, isActive: !link.isActive }, {
      onSuccess: () => toast.success(link.isActive ? t('Link desativado') : t('Link ativado')),
    })
  }

  const handleDelete = (link: ShortLink) => {
    if (!confirm(t('Deletar este link encurtado?'))) return
    deleteMut.mutate(link.id, {
      onSuccess: () => toast.success(t('Link deletado')),
    })
  }

  const openEdit = (link: ShortLink) => {
    setEditLink(link)
    setDialogOpen(true)
  }

  const openCreate = () => {
    setEditLink(null)
    setDialogOpen(true)
  }

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
            <Link2 className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h1 className="text-xl font-bold tracking-tight">{t('Encurtador de Links')}</h1>
            <p className="text-sm text-muted-foreground">{t('Crie links curtos com mascaras de rede social')}</p>
          </div>
        </div>
        <Button size="sm" onClick={openCreate}>
          <Plus className="h-4 w-4 mr-1" /> {t('Novo Link')}
        </Button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-3 gap-3">
        <div className="rounded-xl border border-border bg-card p-3">
          <p className="text-[10px] text-muted-foreground uppercase tracking-wide">{t('Total links')}</p>
          <p className="text-xl font-bold">{stats?.totalLinks ?? 0}</p>
        </div>
        <div className="rounded-xl border border-border bg-card p-3">
          <p className="text-[10px] text-muted-foreground uppercase tracking-wide">{t('Total cliques')}</p>
          <p className="text-xl font-bold">{stats?.totalClicks ?? 0}</p>
        </div>
        <div className="rounded-xl border border-border bg-card p-3">
          <p className="text-[10px] text-muted-foreground uppercase tracking-wide">{t('Links ativos')}</p>
          <p className="text-xl font-bold text-green-500">{stats?.activeLinks ?? 0}</p>
        </div>
      </div>

      {/* Table */}
      {isLoading ? (
        <div className="space-y-3">
          <Skeleton className="h-8 w-full" />
          <Skeleton className="h-48 w-full" />
        </div>
      ) : !links || links.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
          <Link2 className="h-12 w-12 mb-3 opacity-20" />
          <p className="text-sm">{t('Nenhum link criado ainda')}</p>
          <p className="text-xs mb-4">{t('Crie seu primeiro link encurtado')}</p>
          <Button size="sm" onClick={openCreate}>
            <Plus className="h-4 w-4 mr-1" /> {t('Novo Link')}
          </Button>
        </div>
      ) : (
        <div className="rounded-xl border border-border overflow-hidden">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/40">
                  <TableHead className="text-[10px] font-semibold">{t('Link Curto')}</TableHead>
                  <TableHead className="text-[10px] font-semibold">{t('Destino')}</TableHead>
                  <TableHead className="text-[10px] font-semibold text-center w-[100px]">{t('Mascara')}</TableHead>
                  <TableHead className="text-[10px] font-semibold text-center w-[40px]">LP</TableHead>
                  <TableHead className="text-[10px] font-semibold text-center w-[60px]">{t('UTM')}</TableHead>
                  <TableHead className="text-[10px] font-semibold text-center w-[90px]">{t('Cliques')}</TableHead>
                  <TableHead className="text-[10px] font-semibold text-center w-[70px]">{t('Status')}</TableHead>
                  <TableHead className="text-[10px] font-semibold w-[100px]">{t('Criado em')}</TableHead>
                  <TableHead className="text-[10px] font-semibold text-right w-[120px]">{t('Acoes')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {links.map(link => (
                  <TableRow key={link.id} className={cn(!link.isActive && 'opacity-50')}>
                    <TableCell className="text-[11px]">
                      <div className="flex items-center gap-1.5">
                        <div className="flex flex-col">
                          {link.domain && (
                            <span className="text-[9px] text-muted-foreground">{link.domain}</span>
                          )}
                          <code className="bg-muted px-1.5 py-0.5 rounded text-[10px] font-mono">
                            /r/{link.code}
                          </code>
                        </div>
                        <button
                          type="button"
                          onClick={() => copyToClipboard(link)}
                          className="text-muted-foreground hover:text-foreground transition-colors"
                          title={t('Copiar link')}
                        >
                          {copiedId === link.id
                            ? <Check className="h-3 w-3 text-green-500" />
                            : <Copy className="h-3 w-3" />
                          }
                        </button>
                      </div>
                    </TableCell>
                    <TableCell className="text-[11px] max-w-[200px]">
                      <a
                        href={link.targetUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-1 text-muted-foreground hover:text-foreground truncate"
                      >
                        <ExternalLink className="h-3 w-3 shrink-0" />
                        <span className="truncate">{link.targetUrl}</span>
                      </a>
                    </TableCell>
                    <TableCell className="text-center">
                      <Badge variant="outline" className={cn('text-[9px]', maskColors[link.mask])}>
                        {maskLabels[link.mask]}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-center">
                      {link.lpEnabled ? (
                        <Badge variant="outline" className="text-[9px] bg-red-500/10 text-red-500 border-red-500/20">LP</Badge>
                      ) : (
                        <span className="text-[10px] text-muted-foreground">-</span>
                      )}
                    </TableCell>
                    <TableCell className="text-center">
                      {link.utmParams && Object.values(link.utmParams).some(v => v) ? (
                        <Badge variant="secondary" className="text-[9px]">UTM</Badge>
                      ) : (
                        <span className="text-[10px] text-muted-foreground">-</span>
                      )}
                    </TableCell>
                    <TableCell className="text-center">
                      <div className="flex flex-col items-center gap-0.5">
                        <div className="flex items-center gap-1 text-[11px] tabular-nums">
                          <MousePointerClick className="h-3 w-3 text-muted-foreground" />
                          {link.clickCount}
                        </div>
                        {link.lpEnabled && link.lpViewCount > 0 && (
                          <div className="flex items-center gap-1 text-[9px] text-muted-foreground tabular-nums">
                            <Eye className="h-2.5 w-2.5" />
                            {link.lpViewCount}
                          </div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-center">
                      <Badge variant={link.isActive ? 'default' : 'secondary'} className="text-[9px]">
                        {link.isActive ? t('Ativo') : t('Inativo')}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-[10px] text-muted-foreground">
                      {link.createdAt ? fmtDate.format(new Date(link.createdAt)) : '-'}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center justify-end gap-1">
                        <button
                          type="button"
                          onClick={() => toggleActive(link)}
                          className="p-1 rounded hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
                          title={link.isActive ? t('Desativar') : t('Ativar')}
                        >
                          {link.isActive
                            ? <ToggleRight className="h-4 w-4 text-green-500" />
                            : <ToggleLeft className="h-4 w-4" />
                          }
                        </button>
                        <button
                          type="button"
                          onClick={() => openEdit(link)}
                          className="p-1 rounded hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
                          title={t('Editar')}
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDelete(link)}
                          className="p-1 rounded hover:bg-muted transition-colors text-muted-foreground hover:text-destructive"
                          title={t('Deletar')}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      )}

      {dialogOpen && (
        <LinkDialog
          open={dialogOpen}
          onOpenChange={setDialogOpen}
          editLink={editLink}
        />
      )}
    </div>
  )
}
